import React, { useContext, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  InputAdornment,
  OutlinedInput,
  TextField,
  useTheme,
} from '@mui/material'
import './ConditionSelector.css'
import IndicatorParameterDialog from './indicatorParameterDialogInputs/IndicatorParameterDialog'
import _cloneDeep from 'lodash.clonedeep'
import { ConditionType as ConditionBlock } from '../contexts/PatternTreeContext'
import Overlay from '../components/overlay/Overlay'
import ConditionType from './conditions/ConditionType'
import { getConditionTypes } from './conditions'
import { PatternContext } from '../contexts/PatternContext'
import Value from './indicators/Value'
import useIndicatorRHS from '../hooks/useIndicatorRHS'
import CandleTargetSelect from './components/CandleTargetSelect'
import { isCompositeName, resolveSideIndicator } from '../../../common/utils/composite'
interface ConditionSelectorProps {
  condition: ConditionBlock['condition']
  onChangeCondition?: (key: string, value: any) => void
  viewOnly?: boolean
  setConditionIsValid?: (isValid: boolean) => void
  candleMode?: 'target' | 'previous' | null // Which candle targeting mode is active
}

const ConditionSelector: React.FC<ConditionSelectorProps> = ({
  condition,
  onChangeCondition = () => {},
  viewOnly = false,
  setConditionIsValid,
  candleMode = null,
}) => {
  const theme = useTheme()

  const { getIndicatorByValue, startingGroupIndicators } = useContext(PatternContext)

  const { getRHS } = useIndicatorRHS()

  // Registry first, then the side's embedded composite snapshot — so shared/
  // copied patterns referencing indicators this user doesn't own still render
  const resolveLHS = () => resolveSideIndicator(condition?.LHS, getIndicatorByValue)
  const resolveRHS = () => resolveSideIndicator(condition?.RHS, getIndicatorByValue)

  function getComparisonByValue(comparisonName: string | undefined): ConditionType | undefined {
    return getConditionTypes(condition?.LHS?.field)?.find(
      (condition) => condition.value === comparisonName
    )
  }

  useEffect(() => {
    const conditionBlock: ConditionBlock['condition'] = condition

    const LHS = resolveSideIndicator(conditionBlock.LHS, getIndicatorByValue)
    let isValid = false

    if (LHS) {
      const conditionType: ConditionType | undefined = getConditionTypes(
        condition?.LHS?.field
      )?.find((condition) => condition.value === conditionBlock.conditionType)
      if (conditionType) {
        if (conditionType.hasRHS) {
          if (
            resolveSideIndicator(conditionBlock.RHS, getIndicatorByValue) ||
            conditionBlock.RHS?.indicator?.name === 'value'
          ) {
            isValid = true
          } else {
            isValid = false
          }
        } else {
          isValid = true
        }

        // Legacy CHANGED_* conditions without threshold are invalid
        if (conditionType.hasThreshold && conditionBlock.threshold === undefined) {
          isValid = false
        }
      } else {
        isValid = false
      }
    } else {
      isValid = false
    }

    if (setConditionIsValid && condition.isValid !== isValid) {
      setConditionIsValid(isValid)
    }
  }, [condition, getIndicatorByValue, setConditionIsValid])

  function reverseCamelCase(str: string | undefined) {
    if (!str) return str
    const spaced = str.replace(/([A-Z])/g, ' $1')
    const capitalized = spaced.charAt(0).toUpperCase() + spaced.slice(1)
    return capitalized
  }

  function renderInputField(indicatorName: string) {
    const inputType = indicatorName.endsWith('Angle') ? 'angle' : 'value'

    switch (inputType) {
      case 'value':
        return (
          <>
            <OutlinedInput
              disabled={viewOnly}
              value={condition?.RHS?.value ?? 0}
              onChange={(e) => onChangeCondition('RHS.value', e.target.value)}
              type={'number'}
              inputProps={{ step: 0.5, min: 0.5 }}
              id="outlined-adornment-amount"
              startAdornment={<InputAdornment position="start">#</InputAdornment>}
              label="Value"
              className={'condition-selector-input'}
            />
          </>
        )
      case 'angle':
        return (
          <>
            <OutlinedInput
              disabled={viewOnly}
              value={condition?.RHS?.value ?? 0}
              onChange={(e) => onChangeCondition('RHS.value', e.target.value)}
              type={'number'}
              inputProps={{ step: 1, min: -90, max: 90 }}
              id="outlined-adornment-amount"
              endAdornment={<InputAdornment position="start">°</InputAdornment>}
              label="Degrees"
              className={'condition-selector-input'}
            />
          </>
        )
      default:
        break
    }
  }

  return (
    <>
      <Overlay active={viewOnly}>
        <Box display={'flex'} gap={2} data-tour-id="condition-selector">
          {/*
                LHS SELECTION
            */}
          <Box display={'flex'} flexDirection={'column'} gap={1}>
            <Autocomplete
              disabled={viewOnly}
              value={resolveLHS() ?? null}
              onChange={(_, newValue) => {
                if (newValue && newValue.value) {
                  onChangeCondition('LHS.indicator.name', newValue.value)
                  onChangeCondition('LHS.field', newValue.fields[0])
                  onChangeCondition('LHS.indicator.params', _cloneDeep(newValue.params))
                }
              }}
              options={(() => {
                const options: any[] = []
                startingGroupIndicators.forEach((group) => {
                  options.push({ label: group.label, isGroupHeader: true })
                  group.indicators
                    .filter((indicator) => indicator?.fields[0] !== 'value')
                    .forEach((indicator) => options.push(indicator))
                })
                return options
              })()}
              className="condition-selector-input"
              getOptionLabel={(option) => {
                if (!option) return ''
                if (option.isGroupHeader) return option.label
                return option.label.length > 13 && option.nickname ? option.nickname : option.label
              }}
              renderOption={(props, option) => {
                if (option.isGroupHeader) {
                  return (
                    <li
                      {...props}
                      style={{
                        fontWeight: 'bold',
                        backgroundColor: `${theme.palette.background.custom4}`,
                        cursor: 'default',
                        pointerEvents: 'none',
                        paddingLeft: '12px',
                      }}
                    >
                      {option.label}
                    </li>
                  )
                }
                return (
                  <li {...props}>
                    {option.label.length > 13 && option.nickname ? option.nickname : option.label}
                  </li>
                )
              }}
              isOptionEqualToValue={(option, value) => {
                if (option.isGroupHeader || value.isGroupHeader) return false
                return option.value === value.value
              }}
              renderInput={(params) => <TextField {...params} label="Indicator" />}
              sx={{ minWidth: 200 }}
            />
            {resolveLHS()?.fields?.length! > 1 && (
              <Autocomplete
                disabled={viewOnly}
                value={
                  resolveLHS()?.fields?.find((field) => field === condition?.LHS?.field) ?? null
                }
                getOptionLabel={(option) => reverseCamelCase(option)!}
                onChange={(_, newValue) => newValue && onChangeCondition('LHS.field', newValue)}
                options={resolveLHS()?.fields ?? []}
                renderOption={(props, option) => <li {...props}>{reverseCamelCase(option)}</li>}
                className="condition-selector-input"
                renderInput={(params) => <TextField {...params} label="Output" />}
                sx={{ minWidth: 200 }}
              />
            )}

            {condition?.LHS?.indicator?.params &&
              !isCompositeName(condition?.LHS?.indicator?.name) && (
                <IndicatorParameterDialog
                  onIndicatorParameterChange={(key: string, value: any) =>
                    onChangeCondition(`LHS.indicator.params.${key}`, value)
                  }
                  parameters={condition.LHS.indicator.params}
                  indicatorName={resolveLHS()?.label}
                  viewOnly={viewOnly}
                />
              )}

            {resolveLHS() && candleMode === 'target' && (
              <CandleTargetSelect
                candle={condition?.LHS?.candle}
                onChange={(candle) => onChangeCondition('LHS.candle', candle)}
                disabled={viewOnly}
                hideRange={!!getComparisonByValue(condition?.conditionType!)?.hasRHS}
                label="Candle"
              />
            )}
          </Box>

          {/*
                CONDITION TYPE SELECTION
            */}
          <Box display={'flex'} flexDirection={'column'} gap={1}>
            {(condition?.LHS?.field || resolveLHS()?.fields?.length! === 1) && (
              <Autocomplete
                disabled={viewOnly}
                value={getComparisonByValue(condition?.conditionType!) ?? null}
                onChange={(_, newValue) => {
                  if (newValue) {
                    onChangeCondition('conditionType', newValue.value)
                  }
                }}
                options={getConditionTypes(condition?.LHS?.field)}
                getOptionLabel={(option) => {
                  if (!option) return ''
                  return option.label
                }}
                className="condition-selector-input"
                renderInput={(params) => <TextField {...params} label="Condition" />}
                sx={{ minWidth: 200 }}
              />
            )}

            {getComparisonByValue(condition?.conditionType!)?.hasThreshold && (
              <OutlinedInput
                disabled={viewOnly}
                value={condition?.threshold ?? 0}
                onChange={(e) => onChangeCondition('threshold', parseFloat(e.target.value) || 0)}
                type={'number'}
                inputProps={
                  condition?.LHS?.field?.endsWith('Angle')
                    ? { step: 1, min: -90, max: 90 }
                    : { step: 1, min: 0 }
                }
                startAdornment={
                  condition?.LHS?.field?.endsWith('Angle') ? undefined : (
                    <InputAdornment position="start">#</InputAdornment>
                  )
                }
                endAdornment={
                  condition?.LHS?.field?.endsWith('Angle') ? (
                    <InputAdornment position="start">°</InputAdornment>
                  ) : undefined
                }
                label={condition?.LHS?.field?.endsWith('Angle') ? 'Degrees' : undefined}
                className={'condition-selector-input'}
                sx={{ minWidth: 200 }}
              />
            )}
          </Box>

          {/*
                RHS SELECTION
            */}
          <Box display={'flex'} flexDirection={'column'} gap={1}>
            {condition?.conditionType &&
              getComparisonByValue(condition?.conditionType!)?.hasRHS && (
                <Autocomplete
                  disabled={viewOnly}
                  value={resolveRHS() ?? null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      onChangeCondition('RHS.indicator.name', newValue.value)
                      onChangeCondition('RHS.field', newValue.fields[0])
                      onChangeCondition('RHS.indicator.params', _cloneDeep(newValue.params))
                      if (newValue.fields[0] === 'value') {
                        onChangeCondition('RHS.value', 0)
                      }
                    }
                  }}
                  className="condition-selector-input"
                  options={(() => {
                    const lhs = resolveLHS()

                    if (!lhs) return []

                    if (getComparisonByValue(condition?.conditionType!)?.onlyValue) {
                      const label = condition?.LHS?.field?.endsWith('Angle') ? 'Angle' : 'Price'
                      return [{ isGroupHeader: true, label: label }, Value]
                    }

                    const isThresholdCondition = getComparisonByValue(
                      condition?.conditionType!
                    )?.hasThreshold
                    const rhsIndicators = getRHS(lhs, condition.LHS?.field)

                    const options: any[] = []
                    startingGroupIndicators.forEach((group) => {
                      let availableIndicators = group.indicators.filter((indicator) =>
                        rhsIndicators.some((rhsIndicator) => rhsIndicator.value === indicator.value)
                      )

                      if (isThresholdCondition) {
                        availableIndicators = availableIndicators.filter(
                          (indicator) => indicator.fields?.[0] !== 'value'
                        )
                      }

                      if (availableIndicators.length > 0) {
                        const label =
                          group.label === 'Price' && condition?.LHS?.field?.endsWith('Angle')
                            ? 'Angle'
                            : group.label
                        options.push({ label: label, isGroupHeader: true })
                        availableIndicators.forEach((indicator) => options.push(indicator))
                      }
                    })

                    return options
                  })()}
                  getOptionLabel={(option) => {
                    if (!option) return ''
                    if (option.isGroupHeader) return option.label
                    return option.label.length > 10 && option.nickname
                      ? option.nickname
                      : option.label
                  }}
                  renderOption={(props, option) => {
                    if (option.isGroupHeader) {
                      return (
                        <li
                          {...props}
                          style={{
                            fontWeight: 'bold',
                            backgroundColor: `${theme.palette.background.custom4}`,
                            cursor: 'default',
                            pointerEvents: 'none',
                            paddingLeft: '12px',
                          }}
                        >
                          {option.label}
                        </li>
                      )
                    }
                    return (
                      <li {...props}>
                        {option.label.length > 10 && option.nickname
                          ? option.nickname
                          : option.label}
                      </li>
                    )
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (option.isGroupHeader || value.isGroupHeader) return false
                    return option.value === value.value
                  }}
                  renderInput={(params) => <TextField {...params} label="Indicator" />}
                  sx={{ minWidth: 200 }}
                />
              )}

            {condition?.RHS?.indicator && resolveRHS()?.fields[0] === 'value' ? (
              renderInputField(condition!.LHS!.field)
            ) : (
              <>
                {resolveRHS()?.fields?.length! > 1 && (
                  <Autocomplete
                    disabled={viewOnly}
                    value={
                      reverseCamelCase(
                        resolveRHS()?.fields?.find((field) => field === condition?.RHS?.field)
                      ) ?? null
                    }
                    onChange={(_, newValue) => newValue && onChangeCondition('RHS.field', newValue)}
                    options={(resolveRHS()?.fields ?? []).filter((field) =>
                      condition?.LHS?.field?.endsWith('Angle')
                        ? field.endsWith('Angle')
                        : !field.endsWith('Angle')
                    )}
                    renderOption={(props, option) => <li {...props}>{reverseCamelCase(option)}</li>}
                    className="condition-selector-input"
                    renderInput={(params) => <TextField {...params} label="Output" />}
                    sx={{ minWidth: 200 }}
                  />
                )}
              </>
            )}

            {condition?.RHS?.indicator?.params &&
              !isCompositeName(condition?.RHS?.indicator?.name) && (
                <IndicatorParameterDialog
                  onIndicatorParameterChange={(key: string, value: any) =>
                    onChangeCondition(`RHS.indicator.params.${key}`, value)
                  }
                  parameters={condition.RHS.indicator.params}
                  indicatorName={resolveRHS()?.label}
                  viewOnly={viewOnly}
                />
              )}

            {condition?.RHS?.indicator &&
              resolveRHS() &&
              resolveRHS()?.fields[0] !== 'value' &&
              candleMode === 'target' && (
                <CandleTargetSelect
                  candle={condition?.RHS?.candle}
                  onChange={(candle) => onChangeCondition('RHS.candle', candle)}
                  disabled={viewOnly}
                  label="Candle"
                />
              )}
          </Box>
        </Box>
      </Overlay>
    </>
  )
}

export default ConditionSelector
